import { useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { basisRefusal } from "@/accounts-payable/query";
import { useResolveAgentAction } from "@/accounts-payable/query";
import type { ApBasisRefusal } from "@/accounts-payable/types";
import { getErrorMessage } from "@/core/api/errors";
import { BasisChangedDialog } from "./basis-changed-dialog";

type Decision = "approved" | "rejected";

/**
 * Approve or reject an action, handling the API's ServiceTitan re-check on
 * approve: a 409 `basis_changed` asks the reviewer (through `dialog`, which the
 * caller renders) whether to approve against the current values, and a 422
 * `basis_blocked` says why it can't be approved now. Either way the rows pick
 * up what changed, since the mutation refetches them on error.
 *
 * Each call handles its own outcome through its promise: per-call `mutate`
 * callbacks only fire for the latest call, so approving a second action before
 * the first answered would lose the first one's dialog. `resolvingIds` holds
 * the actions with a request in flight, so their rows can say so and a second
 * click on one is ignored.
 */
export const useResolveWithBasisCheck = (businessId: string | null) => {
  const resolve = useResolveAgentAction(businessId);
  const [confirming, setConfirming] = useState<{
    actionId: string;
    refusal: ApBasisRefusal;
  } | null>(null);
  const [resolvingIds, setResolvingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const track = (actionId: string, request: Promise<unknown>) => {
    setResolvingIds((ids) => new Set(ids).add(actionId));
    void request.finally(() =>
      setResolvingIds((ids) => {
        const next = new Set(ids);
        next.delete(actionId);
        return next;
      }),
    );
  };

  const onApproveError = (actionId: string) => (error: unknown) => {
    const refusal = basisRefusal(error);
    if (refusal?.code === "basis_changed") {
      setConfirming({ actionId, refusal });
      return;
    }
    setConfirming(null);
    if (refusal?.code === "basis_blocked") {
      toast.error("This can't be approved now", {
        description: `${refusal.error} Re-run the agent for proposals that fit ServiceTitan as it is now.`,
      });
      return;
    }
    toast.error("Couldn't approve the action", {
      description: getErrorMessage(error),
    });
  };

  const resolveAction = (actionId: string, decision: Decision) => {
    if (resolvingIds.has(actionId)) return;
    track(
      actionId,
      resolve.mutateAsync({ actionId, decision }).catch(
        decision === "approved"
          ? onApproveError(actionId)
          : (error: unknown) =>
              toast.error("Couldn't dismiss the action", {
                description: getErrorMessage(error),
              }),
      ),
    );
  };

  const confirm = () => {
    if (!confirming) return;
    const { actionId, refusal } = confirming;
    track(
      actionId,
      resolve
        .mutateAsync({
          actionId,
          decision: "approved",
          acknowledgedBasisDigest: refusal.basis_digest,
        })
        .then(() => setConfirming(null), onApproveError(actionId)),
    );
  };

  const dialog = (
    <BasisChangedDialog
      refusal={confirming?.refusal ?? null}
      isPending={confirming !== null && resolvingIds.has(confirming.actionId)}
      onCancel={() => setConfirming(null)}
      onConfirm={confirm}
    />
  );

  return { resolve: resolveAction, resolvingIds, dialog };
};
